import {
  Box,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  TextField,
  CircularProgress,
} from '@mui/material'
import { Button } from '@ds/components'
import { useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { type Control, type FieldErrors, Controller } from 'react-hook-form'
import { PasswordRequirements } from '../PasswordRequirements'
import { REGISTER_FORM_STRINGS } from './strings'

type TRegisterFormData = {
  password: string
  confirmPassword: string
}

type TPasswordValidation = {
  length: boolean
  uppercase: boolean
  lowercase: boolean
  digit: boolean
}

type TRegisterFormProps = {
  control: Control<TRegisterFormData>
  errors: FieldErrors<TRegisterFormData>
  isValid: boolean
  isLoading: boolean
  passwordValidation: TPasswordValidation
  watchedPassword: string
  onSubmit: (e: React.FormEvent) => void
}

export const RegisterForm = ({
  control,
  errors,
  isValid,
  isLoading,
  passwordValidation,
  watchedPassword,
  onSubmit,
}: TRegisterFormProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 12, sm: 8, md: 6 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4, md: 5 },
          minWidth: { xs: '90%', sm: 400, md: 480 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
          {REGISTER_FORM_STRINGS.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {REGISTER_FORM_STRINGS.description}
        </Typography>
        <Box component="form" sx={{ width: '100%' }} onSubmit={onSubmit}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: REGISTER_FORM_STRINGS.passwordRequired,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={REGISTER_FORM_STRINGS.passwordLabel}
                variant="filled"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="new-password"
                error={!!errors.password}
                helperText={errors.password?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />

          <PasswordRequirements validation={passwordValidation} />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: REGISTER_FORM_STRINGS.confirmPasswordRequired,
              validate: value => {
                if (value !== watchedPassword) {
                  return REGISTER_FORM_STRINGS.passwordsDoNotMatch
                }
                return true
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={REGISTER_FORM_STRINGS.confirmPasswordLabel}
                variant="filled"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                sx={{
                  mb: 3,
                  '& .MuiFormHelperText-root': {
                    minHeight: '20px',
                    margin: '4px 0 0 0',
                  },
                }}
                autoComplete="new-password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message || ' '}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(show => !show)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ px: 3 }}
              disabled={isLoading || !isValid}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isLoading
                ? REGISTER_FORM_STRINGS.settingPasswordButton
                : REGISTER_FORM_STRINGS.setPasswordButton}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
